import * as React from "react";
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useMIS } from "../MISContext";
import { getPlantMis } from "../../../../../../api/misApi";
import { useParams } from "react-router-dom";
import StatCard from "../../../../../../components/StatCard";

const formatMoney = (value) =>
  `$${(Number(value) || 0).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  })}`;

const formatPercent = (value) =>
  `${(Number(value) || 0).toLocaleString("en-US", {
    maximumFractionDigits: 1,
  })}%`;

// Total invoiced across the three tasks - each task keeps its invoices as
// inv1, inv2, … keys on the MIS invoices record.
const sumInvoiceAmounts = (invoicesData) => {
  let total = 0;
  ["engineering", "procurement", "fabrication"].forEach((task) => {
    const taskData = invoicesData?.[task];
    if (!taskData) return;
    Object.keys(taskData).forEach((key) => {
      if (/^inv\d+$/.test(key) && taskData[key]?.amount) {
        total += Number(taskData[key].amount) || 0;
      }
    });
  });
  return total;
};

export default function ProfitLossTab() {
  const { plantId } = useParams();
  const { totalAvgProgress } = useMIS();
  const [projectCost, setProjectCost] = React.useState(0);
  const [invoiceAmount, setInvoiceAmount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadMisData = async () => {
      if (!plantId) return;
      try {
        setLoading(true);
        const misData = await getPlantMis(plantId);
        const invoicesData = misData?.data?.data?.invoices;
        if (invoicesData) {
          if (invoicesData.projectCost !== undefined) {
            setProjectCost(Number(invoicesData.projectCost) || 0);
          }
          setInvoiceAmount(sumInvoiceAmounts(invoicesData));
        }
      } catch (error) {
        console.error("Failed to load MIS data for Profit & Loss:", error);
      } finally {
        setLoading(false);
      }
    };
    loadMisData();
  }, [plantId]);

  const progressPercent = Number(totalAvgProgress) || 0;
  const earnedValues = (projectCost * progressPercent) / 100;
  const difference = earnedValues - invoiceAmount;
  const differenceColor = difference >= 0 ? "success.main" : "error.main";

  const rows = [
    { no: 1, description: "Project cost", value: formatMoney(projectCost) },
    {
      no: 2,
      description: "Amount % progress",
      value: formatPercent(progressPercent),
    },
    { no: 3, description: "Earned value", value: formatMoney(earnedValues) },
    { no: 4, description: "Invoice amount", value: formatMoney(invoiceAmount) },
    { no: 5, description: "Difference", value: formatMoney(difference) },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            moduleKey="mis-profit-loss"
            label="Project cost"
            value={formatMoney(projectCost)}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            moduleKey="mis-progress"
            label="Earned value"
            value={formatMoney(earnedValues)}
            hint={`${formatPercent(progressPercent)} progress`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            moduleKey="mis-invoices"
            label="Invoiced"
            value={formatMoney(invoiceAmount)}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            moduleKey="mis-deviation"
            label="Earned − invoiced"
            value={
              <Box component="span" sx={{ color: differenceColor }}>
                {formatMoney(difference)}
              </Box>
            }
            hint={difference >= 0 ? "Ahead of invoicing" : "Invoiced ahead of progress"}
            loading={loading}
          />
        </Grid>
      </Grid>

      <Paper sx={{ maxWidth: 560, overflow: "hidden" }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="h5">Profit &amp; Loss statement</Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 50 }}>#</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right" sx={{ width: 160 }}>
                  Amount
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const isTotal = row.no === 5;
                return (
                  <TableRow key={row.no}>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {row.no}
                    </TableCell>
                    <TableCell sx={{ fontWeight: isTotal ? 700 : 400 }}>
                      {row.description}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: isTotal ? 700 : 500,
                        color: isTotal ? differenceColor : "text.primary",
                      }}
                    >
                      {row.value}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
